// A time of day typed in two numeric fields, hours and minutes (SPEC-23,
// Sono's bedtime). It hands the flow "hh:mm" at every keystroke, a field of
// one digit padded to two, and the lib's answerError decides whether that
// is a time: « 25:00 » stays refused and keeps "Terminar" off.

import { useRef, useState } from "react";
import { answerError } from "poweryourflow-lib";
import type { Question } from "poweryourflow-lib";

import { copy } from "../i18n/pt-PT";

const text = copy.questionnaire;

const field =
  "h-[var(--control-h)] w-20 rounded-pill border-[1.5px] border-line-20 bg-white text-center text-title-lg font-medium text-dark " +
  "outline-none transition-colors duration-[var(--dur-micro)] ease-pyf focus:border-violet aria-invalid:border-orange";

/** At most two digits; anything else she types is dropped. */
const digits = (typed: string) => typed.replace(/\D/g, "").slice(0, 2);
const pad = (part: string) => (part.length === 1 ? `0${part}` : part);
const compose = (hours: string, minutes: string) => `${pad(hours)}:${pad(minutes)}`;

export function TimeInput({
  question,
  value,
  onChange,
}: {
  question: Question;
  value: string | undefined;
  onChange: (value: string) => void;
}) {
  const [hours, setHours] = useState(() => value?.split(":")[0] ?? "");
  const [minutes, setMinutes] = useState(() => value?.split(":")[1] ?? "");
  const minutesField = useRef<HTMLInputElement>(null);

  const composed = compose(hours, minutes);
  const invalid = hours !== "" && minutes !== "" && answerError(question, composed) !== undefined;

  const change = (nextHours: string, nextMinutes: string) => {
    setHours(nextHours);
    setMinutes(nextMinutes);
    onChange(compose(nextHours, nextMinutes));
  };

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="pb-3 text-body-sm text-muted">{text.hints.time}</legend>
      <div className="flex items-center gap-3">
        <input
          aria-label={text.hour}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          maxLength={2}
          placeholder={text.hourPlaceholder}
          value={hours}
          aria-invalid={invalid || undefined}
          onChange={(event) => {
            const typed = digits(event.target.value);
            change(typed, minutes);
            // Two digits of hours: on to the minutes, as on a clock.
            if (typed.length === 2) minutesField.current?.focus();
          }}
          className={field}
        />
        <span aria-hidden="true" className="text-title-lg font-medium text-dark">
          :
        </span>
        <input
          ref={minutesField}
          aria-label={text.minutes}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          maxLength={2}
          placeholder={text.minutesPlaceholder}
          value={minutes}
          aria-invalid={invalid || undefined}
          onChange={(event) => change(hours, digits(event.target.value))}
          className={field}
        />
      </div>
      {invalid && (
        <p role="alert" className="text-caption text-orange-ink">
          {text.timeInvalid}
        </p>
      )}
    </fieldset>
  );
}
